import { Renderer } from '@client/core/Renderer';
import { AssetLoader } from '@client/core/AssetLoader';
import { Inventory } from '@client/inventory/Inventory';
import { Player } from '@client/entities/Player';
import { GameItem, getItemColor } from '@shared/items/Item';
import { EquipmentSlot, EquipmentItem } from '@shared/types';
import { ItemTooltip } from './ItemTooltip';
import { SLOT_FALLBACK_ICONS } from './ItemIcons';
import { getProceduralIcon } from '@client/core/ProceduralItemIcons';
import { t } from '@locales/index';

type Tab = 'inventory' | 'stats';

interface SlotInfo {
  x: number;
  y: number;
  size: number;
  index: number;
  type: 'inventory' | 'equipment';
  equipSlot?: EquipmentSlot;
}

export class InventoryUI {
  private player: Player;
  private inventory: Inventory;
  private assetLoader: AssetLoader;
  private isOpen: boolean = false;
  private fadeAlpha: number = 0;
  private width: number;
  private height: number;

  // Responsive sizing
  private invSlotSize: number = 40;
  private equipSlotSize: number = 50;
  private cols: number = 10;
  private rows: number = 6;

  // UI State
  private activeTab: Tab = 'inventory';
  private selectedSlot: SlotInfo | null = null;
  private hoveredSlot: SlotInfo | null = null;
  private mouseX: number = 0;
  private mouseY: number = 0;
  private tooltip: ItemTooltip = new ItemTooltip();

  // Panel dimensions
  private panelX: number = 0;
  private panelY: number = 0;
  private panelW: number = 0;
  private panelH: number = 0;

  // Equipment section width
  private equipSectionWidth: number = 200;

  constructor(player: Player, screenWidth: number, screenHeight: number, assetLoader: AssetLoader) {
    this.player = player;
    this.inventory = player.inventory;
    this.assetLoader = assetLoader;
    this.width = screenWidth;
    this.height = screenHeight;
    this.calculateLayout();
  }

  private calculateLayout(): void {
    // Full screen layout for both mobile and desktop
    const margin = 15;

    this.panelW = this.width - margin * 2;
    this.panelH = this.height - margin * 2;
    this.panelX = margin;
    this.panelY = margin;

    const totalSlots = 60;
    const gap = 4;
    const headerHeight = 95; // Header + tabs
    const footerHeight = 30;
    const availableHeight = this.panelH - headerHeight - footerHeight;

    const isMobile = this.width < 600;

    if (isMobile) {
      // Mobile: Equipment smaller, inventory below
      this.equipSlotSize = 40;
      this.equipSectionWidth = 3 * (this.equipSlotSize + 8) + 30;

      // Calculate inventory grid to fit 60 slots
      const invWidth = this.panelW - 30;
      const invHeight = availableHeight - (4 * (this.equipSlotSize + 18)) - 30;

      // Find optimal cols/rows for 60 slots
      this.cols = Math.floor(invWidth / 44);
      this.cols = Math.max(5, this.cols);
      this.rows = Math.ceil(totalSlots / this.cols);
      this.invSlotSize = Math.min(
        Math.floor((invWidth - (this.cols - 1) * gap) / this.cols),
        Math.floor((invHeight - (this.rows - 1) * gap) / this.rows)
      );
      this.invSlotSize = Math.max(32, Math.min(50, this.invSlotSize));
    } else {
      // Desktop: Side by side layout
      // Equipment section - make slots bigger
      this.equipSlotSize = Math.min(55, Math.floor(availableHeight / 5.5));
      this.equipSlotSize = Math.max(45, this.equipSlotSize);
      this.equipSectionWidth = 3 * (this.equipSlotSize + 8) + 40;

      // Inventory section - calculate to fit 60 slots
      const invWidth = this.panelW - this.equipSectionWidth - 40;
      const invHeight = availableHeight - 10;

      // Calculate optimal grid for 60 slots
      // Try to maximize slot size while fitting all 60 slots
      let bestSlotSize = 0;
      let bestCols = 10;
      let bestRows = 6;

      for (let c = 6; c <= 15; c++) {
        const r = Math.ceil(totalSlots / c);
        const maxSlotW = Math.floor((invWidth - (c - 1) * gap) / c);
        const maxSlotH = Math.floor((invHeight - (r - 1) * gap) / r);
        const slotS = Math.min(maxSlotW, maxSlotH);

        if (slotS > bestSlotSize) {
          bestSlotSize = slotS;
          bestCols = c;
          bestRows = r;
        }
      }

      this.cols = bestCols;
      this.rows = bestRows;
      this.invSlotSize = Math.max(32, Math.min(60, bestSlotSize));
    }
  }

  setScreenSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.calculateLayout();
  }

  toggle(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.fadeAlpha = 0;
    } else {
      this.selectedSlot = null;
      this.hoveredSlot = null;
    }
  }

  open(): void { this.isOpen = true; this.fadeAlpha = 0; }
  close(): void { this.isOpen = false; this.selectedSlot = null; }
  isVisible(): boolean { return this.isOpen; }

  handleMouseMove(x: number, y: number): void {
    if (!this.isOpen) return;
    this.mouseX = x;
    this.mouseY = y;
    this.hoveredSlot = this.getSlotAt(x, y);

    if (this.hoveredSlot) {
      const item = this.getItemInSlot(this.hoveredSlot);
      if (item) {
        let compareItem: EquipmentItem | null = null;
        if ('slot' in item) {
          compareItem = this.inventory.getEquippedItem((item as EquipmentItem).slot) || null;
        }
        this.tooltip.show(item, x, y, compareItem);
      } else {
        this.tooltip.hide();
      }
    } else {
      this.tooltip.hide();
    }
  }

  handleMouseDown(x: number, y: number): void {
    if (!this.isOpen) return;

    // Check tab clicks
    const tabY = this.panelY + 45;
    const tabH = 32;
    if (y >= tabY && y <= tabY + tabH) {
      const tabs: Tab[] = ['inventory', 'stats'];
      const tabW = 100;
      for (let i = 0; i < tabs.length; i++) {
        const tx = this.panelX + 15 + i * (tabW + 5);
        if (x >= tx && x <= tx + tabW) {
          this.activeTab = tabs[i];
          this.selectedSlot = null;
          return;
        }
      }
    }

    // Check close button
    const closeX = this.panelX + this.panelW - 40;
    const closeY = this.panelY + 8;
    if (x >= closeX && x <= closeX + 32 && y >= closeY && y <= closeY + 32) {
      this.close();
      return;
    }

    const slot = this.getSlotAt(x, y);
    if (slot) {
      this.selectedSlot = slot;
    }
  }

  handleMouseUp(x: number, y: number): void {
    if (!this.isOpen || !this.selectedSlot) return;

    const targetSlot = this.getSlotAt(x, y);

    if (targetSlot && this.selectedSlot) {
      this.handleSlotInteraction(this.selectedSlot, targetSlot);
    }

    this.selectedSlot = null;
  }

  handleRightClick(x: number, y: number): void {
    if (!this.isOpen) return;

    const slot = this.getSlotAt(x, y);
    if (!slot) return;

    const item = this.getItemInSlot(slot);
    if (!item) return;

    if (slot.type === 'inventory' && 'slot' in item) {
      this.equipItem(slot.index, item as EquipmentItem);
    } else if (slot.type === 'equipment' && slot.equipSlot) {
      this.unequipItem(slot.equipSlot);
    }
  }

  private handleSlotInteraction(source: SlotInfo, target: SlotInfo): void {
    if (source.type === target.type && source.index === target.index) return;

    if (source.type === 'inventory' && target.type === 'equipment') {
      const item = this.inventory.getItem(source.index);
      if (item && 'slot' in item && (item as EquipmentItem).slot === target.equipSlot) {
        this.equipItem(source.index, item as EquipmentItem);
      }
    } else if (source.type === 'equipment' && target.type === 'inventory') {
      if (source.equipSlot) {
        this.unequipItem(source.equipSlot);
      }
    }
  }

  private equipItem(index: number, item: EquipmentItem): void {
    const prev = this.inventory.equipItem(item);
    this.inventory.removeItem(index);
    if (prev) this.inventory.addItem(prev);
  }

  private unequipItem(slot: EquipmentSlot): void {
    if (this.inventory.isFull()) return;
    const item = this.inventory.unequipItem(slot);
    if (item) this.inventory.addItem(item);
  }

  private getSlotAt(x: number, y: number): SlotInfo | null {
    if (this.activeTab === 'inventory') {
      // Check equipment slots first
      const equipSlot = this.getEquipmentSlotAt(x, y);
      if (equipSlot) return equipSlot;

      // Then inventory slots
      return this.getInventorySlotAt(x, y);
    }
    return null;
  }

  private getInventorySlotAt(x: number, y: number): SlotInfo | null {
    const layout = this.getInventoryLayout();
    const gap = 4;
    const slotS = layout.slotSize;
    const maxSlots = this.inventory.getMaxSlots();
    const actualRows = Math.ceil(maxSlots / this.cols);

    for (let row = 0; row < actualRows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const index = row * this.cols + col;
        if (index >= maxSlots) continue;

        const sx = layout.x + col * (slotS + gap);
        const sy = layout.y + row * (slotS + gap);

        if (x >= sx && x <= sx + slotS && y >= sy && y <= sy + slotS) {
          return {
            x: sx, y: sy,
            size: slotS,
            index: index,
            type: 'inventory'
          };
        }
      }
    }
    return null;
  }

  private getEquipmentSlotAt(x: number, y: number): SlotInfo | null {
    const slots = this.getEquipmentSlotPositions();
    const equipSize = this.equipSlotSize;

    for (const [equipSlot, pos] of slots) {
      if (x >= pos.x && x <= pos.x + equipSize &&
          y >= pos.y && y <= pos.y + equipSize) {
        return {
          x: pos.x, y: pos.y,
          size: equipSize,
          index: 0,
          type: 'equipment',
          equipSlot
        };
      }
    }
    return null;
  }

  private getInventoryLayout(): { x: number; y: number; w: number; h: number; slotSize: number } {
    // Inventory is on the right side after the divider
    const gap = 4;
    const slotS = this.invSlotSize;
    const invW = this.cols * (slotS + gap);
    const invH = this.rows * (slotS + gap);

    return {
      x: this.panelX + this.equipSectionWidth + 20,
      y: this.panelY + 95,
      w: invW,
      h: invH,
      slotSize: slotS
    };
  }

  private getEquipmentSlotPositions(): Map<EquipmentSlot, { x: number; y: number }> {
    const equipX = this.panelX + 20;
    const equipY = this.panelY + 95;
    const s = this.equipSlotSize;
    const gapX = 8;
    const gapY = 20; // Vertical gap for labels

    const positions = new Map<EquipmentSlot, { x: number; y: number }>();

    // Paper doll style layout - 3 columns
    // Row 1: Pelerin | Kask | Kolye
    positions.set('cape', { x: equipX, y: equipY });
    positions.set('head', { x: equipX + s + gapX, y: equipY });
    positions.set('amulet', { x: equipX + (s + gapX) * 2, y: equipY });

    // Row 2: Silah | Zırh | Kalkan
    positions.set('weapon', { x: equipX, y: equipY + s + gapY });
    positions.set('chest', { x: equipX + s + gapX, y: equipY + s + gapY });
    positions.set('offhand', { x: equipX + (s + gapX) * 2, y: equipY + s + gapY });

    // Row 3: Yüzük1 | Kemer | Yüzük2
    positions.set('ring1', { x: equipX, y: equipY + (s + gapY) * 2 });
    positions.set('belt', { x: equipX + s + gapX, y: equipY + (s + gapY) * 2 });
    positions.set('ring2', { x: equipX + (s + gapX) * 2, y: equipY + (s + gapY) * 2 });

    // Row 4: Eldiven | Bot
    positions.set('gloves', { x: equipX, y: equipY + (s + gapY) * 3 });
    positions.set('boots', { x: equipX + s + gapX, y: equipY + (s + gapY) * 3 });

    return positions;
  }

  private getItemInSlot(slot: SlotInfo): GameItem | null {
    if (slot.type === 'inventory') {
      return this.inventory.getItem(slot.index);
    } else if (slot.equipSlot) {
      return this.inventory.getEquippedItem(slot.equipSlot);
    }
    return null;
  }

  render(renderer: Renderer): void {
    if (!this.isOpen) return;

    // Animate fade-in
    if (this.fadeAlpha < 1) {
      this.fadeAlpha = Math.min(1, this.fadeAlpha + 0.08);
    }

    const ctx = renderer.context;
    ctx.save();
    ctx.globalAlpha = this.fadeAlpha;

    // Dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.fillRect(0, 0, this.width, this.height);

    // Main panel
    this.drawPanel(ctx);
    this.drawHeader(ctx);
    this.drawTabs(ctx);

    if (this.activeTab === 'inventory') {
      this.drawCombinedView(ctx);
    } else {
      this.drawStatsTab(ctx);
    }

    // Tooltip
    this.tooltip.render(ctx, this.width, this.height);

    this.drawFooter(ctx);

    ctx.restore(); // restore globalAlpha from fade-in
  }

  private drawPanel(ctx: CanvasRenderingContext2D): void {
    const grad = ctx.createLinearGradient(this.panelX, this.panelY, this.panelX, this.panelY + this.panelH);
    grad.addColorStop(0, '#1c1916');
    grad.addColorStop(1, '#0d0c0a');
    ctx.fillStyle = grad;

    ctx.beginPath();
    ctx.roundRect(this.panelX, this.panelY, this.panelW, this.panelH, 10);
    ctx.fill();

    ctx.strokeStyle = '#3d3429';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  private drawHeader(ctx: CanvasRenderingContext2D): void {
    // Title
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('ENVANTER', this.panelX + 15, this.panelY + 24);

    // Gold
    ctx.fillStyle = '#ffd700';
    ctx.font = '14px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(`💰 ${this.inventory.getGold().toLocaleString()}`, this.panelX + this.panelW - 50, this.panelY + 24);

    // Close button
    const closeX = this.panelX + this.panelW - 40;
    const closeY = this.panelY + 8;
    const isCloseHovered = this.mouseX >= closeX && this.mouseX <= closeX + 32 &&
                           this.mouseY >= closeY && this.mouseY <= closeY + 32;

    ctx.fillStyle = isCloseHovered ? '#ff4444' : '#666';
    ctx.font = 'bold 18px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('✕', closeX + 16, closeY + 18);
  }

  private drawTabs(ctx: CanvasRenderingContext2D): void {
    const tabs: { id: Tab; label: string }[] = [
      { id: 'inventory', label: t('inventory.items') },
      { id: 'stats', label: t('inventory.statistics') }
    ];

    const tabY = this.panelY + 45;
    const tabW = 100;
    const tabH = 32;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const tabX = this.panelX + 15 + i * (tabW + 5);
      const isActive = this.activeTab === tab.id;
      const isHovered = this.mouseX >= tabX && this.mouseX <= tabX + tabW &&
                        this.mouseY >= tabY && this.mouseY <= tabY + tabH;

      ctx.fillStyle = isActive ? '#2a2520' : isHovered ? '#1f1c18' : '#151410';
      ctx.beginPath();
      ctx.roundRect(tabX, tabY, tabW, tabH, [6, 6, 0, 0]);
      ctx.fill();

      if (isActive) {
        ctx.fillStyle = '#ffd700';
        ctx.fillRect(tabX + 8, tabY + tabH - 2, tabW - 16, 2);
      }

      ctx.fillStyle = isActive ? '#ffd700' : '#888';
      ctx.font = isActive ? 'bold 13px Arial' : '13px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tab.label, tabX + tabW / 2, tabY + tabH / 2);
    }
  }

  private drawCombinedView(ctx: CanvasRenderingContext2D): void {
    // Draw equipment section (left)
    this.drawEquipmentSection(ctx);

    // Draw divider line
    const divX = this.panelX + this.equipSectionWidth + 5;
    ctx.strokeStyle = '#2a2520';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(divX, this.panelY + 82);
    ctx.lineTo(divX, this.panelY + this.panelH - 30);
    ctx.stroke();

    // Draw inventory section (right)
    this.drawInventorySection(ctx);
  }

  private drawEquipmentSection(ctx: CanvasRenderingContext2D): void {
    // Section title
    ctx.fillStyle = '#999';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('inventory.equipment'), this.panelX + 20, this.panelY + 82);

    const slots = this.getEquipmentSlotPositions();
    const equipSize = this.equipSlotSize;

    for (const [equipSlot, pos] of slots) {
      const item = this.inventory.getEquippedItem(equipSlot);
      const isHovered = this.hoveredSlot?.equipSlot === equipSlot;
      const isSelected = this.selectedSlot?.equipSlot === equipSlot;

      this.drawSlot(ctx, pos.x, pos.y, equipSize, item, isHovered, isSelected, equipSlot);

      // Draw slot label below
      ctx.fillStyle = isHovered ? '#ddd' : '#777';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(this.getSlotLabel(equipSlot), pos.x + equipSize / 2, pos.y + equipSize + 3);
    }
  }

  private drawInventorySection(ctx: CanvasRenderingContext2D): void {
    const layout = this.getInventoryLayout();
    const gap = 4;
    const slotS = layout.slotSize;
    const maxSlots = this.inventory.getMaxSlots();

    // Section title
    ctx.fillStyle = '#999';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('inventory.bag'), layout.x, this.panelY + 82);

    ctx.fillStyle = '#666';
    ctx.font = '11px Arial';
    ctx.fillText(`(${this.inventory.getUsedSlots()}/${maxSlots})`, layout.x + 50, this.panelY + 82);

    const items = this.inventory.getAllItems();

    // Calculate actual rows needed for all slots
    const actualRows = Math.ceil(maxSlots / this.cols);

    // Draw grid background
    ctx.fillStyle = 'rgba(0,0,0,0.3)';
    ctx.beginPath();
    ctx.roundRect(
      layout.x - 6,
      layout.y - 6,
      this.cols * (slotS + gap) + 8,
      actualRows * (slotS + gap) + 8,
      8
    );
    ctx.fill();

    for (let row = 0; row < actualRows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const index = row * this.cols + col;
        if (index >= maxSlots) continue;

        const sx = layout.x + col * (slotS + gap);
        const sy = layout.y + row * (slotS + gap);
        const item = items[index];

        const isHovered = this.hoveredSlot?.type === 'inventory' && this.hoveredSlot.index === index;
        const isSelected = this.selectedSlot?.type === 'inventory' && this.selectedSlot.index === index;

        this.drawSlot(ctx, sx, sy, slotS, item, isHovered, isSelected);
      }
    }
  }

  private drawStatsTab(ctx: CanvasRenderingContext2D): void {
    const startX = this.panelX + 20;
    const startY = this.panelY + 95;
    const lineH = 24;
    const colW = (this.panelW - 50) / 2;

    // Main stats section
    ctx.fillStyle = '#888';
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('inventory.basicStats'), startX, startY - 8);

    const mainStats = [
      { label: t('combat.levelUp').replace('!', ''), value: this.player.level.toString(), color: '#ffd700' },
      { label: t('stats.hp'), value: `${Math.ceil(this.player.stats.hp)}/${this.player.stats.maxHp}`, color: '#e74c3c' },
      { label: t('stats.mana'), value: `${Math.ceil(this.player.stats.mana)}/${this.player.stats.maxMana}`, color: '#3498db' },
      { label: t('stats.armor'), value: this.player.stats.armor.toString(), color: '#95a5a6' },
      { label: t('stats.evasion'), value: this.player.stats.evasion.toString(), color: '#7f8c8d' },
      { label: t('stats.strength'), value: this.player.stats.strength.toString(), color: '#e67e22' },
      { label: t('stats.agility'), value: this.player.stats.agility.toString(), color: '#2ecc71' },
      { label: t('stats.spirit'), value: this.player.stats.spirit.toString(), color: '#9b59b6' },
    ];

    for (let i = 0; i < mainStats.length; i++) {
      const col = i < 4 ? 0 : 1;
      const row = i % 4;
      const x = startX + col * colW;
      const y = startY + row * lineH;
      const stat = mainStats[i];

      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.beginPath();
      ctx.roundRect(x - 3, y - 6, colW - 8, 20, 3);
      ctx.fill();

      ctx.fillStyle = '#666';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(stat.label, x, y);

      ctx.fillStyle = stat.color;
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(stat.value, x + colW - 15, y);
    }

    // Resistances section
    const resistY = startY + lineH * 4 + 20;
    ctx.fillStyle = '#888';
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('inventory.resistances'), startX, resistY - 8);

    const resistances = [
      { label: t('inventory.gokResist'), value: `${this.player.resistances.gok}%`, color: '#00bfff' },
      { label: t('inventory.odResist'), value: `${this.player.resistances.od}%`, color: '#ff6b35' },
      { label: t('inventory.suResist'), value: `${this.player.resistances.su}%`, color: '#4fc3f7' },
      { label: t('inventory.karanlikResist'), value: `${this.player.resistances.karanlik}%`, color: '#9b59b6' },
    ];

    for (let i = 0; i < resistances.length; i++) {
      const col = i < 3 ? 0 : 1;
      const row = i < 3 ? i : i - 3;
      const x = startX + col * colW;
      const y = resistY + row * lineH;
      const res = resistances[i];

      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.beginPath();
      ctx.roundRect(x - 3, y - 6, colW - 8, 20, 3);
      ctx.fill();

      ctx.fillStyle = '#666';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(res.label, x, y);

      // Color based on value
      const resValue = parseInt(res.value);
      let valueColor = res.color;
      if (resValue < 0) valueColor = '#ff4444';
      else if (resValue >= 75) valueColor = '#44ff44';

      ctx.fillStyle = valueColor;
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(res.value, x + colW - 15, y);
    }

    // Combat stats section
    const combatY = resistY + lineH * 3 + 20;
    ctx.fillStyle = '#888';
    ctx.font = '10px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('inventory.combatStats'), startX, combatY - 8);

    const combatStats = [
      { label: t('inventory.attackDamage'), value: this.player.attackDamage.toString(), color: '#ff7675' },
      { label: t('stats.attackSpeed'), value: `${this.player.attackSpeed.toFixed(1)}/s`, color: '#fdcb6e' },
      { label: t('skillsV2.sliders.range'), value: this.player.attackRange.toString(), color: '#81ecec' },
      { label: t('stats.evasion'), value: `${this.player.dodgeCharges}/${this.player.maxDodgeCharges}`, color: '#a29bfe' },
    ];

    for (let i = 0; i < combatStats.length; i++) {
      const col = i < 2 ? 0 : 1;
      const row = i % 2;
      const x = startX + col * colW;
      const y = combatY + row * lineH;
      const stat = combatStats[i];

      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.beginPath();
      ctx.roundRect(x - 3, y - 6, colW - 8, 20, 3);
      ctx.fill();

      ctx.fillStyle = '#666';
      ctx.font = '11px Arial';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(stat.label, x, y);

      ctx.fillStyle = stat.color;
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'right';
      ctx.fillText(stat.value, x + colW - 15, y);
    }
  }

  private drawSlot(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, size: number,
    item: GameItem | null,
    isHovered: boolean,
    isSelected: boolean,
    equipSlot?: EquipmentSlot
  ): void {
    // Background
    ctx.fillStyle = isSelected ? '#3a3530' : isHovered ? '#2a2520' : '#1a1815';
    ctx.beginPath();
    ctx.roundRect(x, y, size, size, 5);
    ctx.fill();

    // Border
    ctx.strokeStyle = isSelected ? '#ffd700' : isHovered ? '#554433' : '#2a2520';
    ctx.lineWidth = isSelected ? 2 : 1;
    ctx.stroke();

    if (item) {
      const color = getItemColor(item.rarity);
      const pad = 4;
      const iconSize = size - pad * 2;

      // Rarity glow background
      ctx.fillStyle = color + '25';
      ctx.beginPath();
      ctx.roundRect(x + 2, y + 2, size - 4, size - 4, 4);
      ctx.fill();

      // Try to draw sprite icon (custom folder → mapped → procedural → legacy)
      let iconDrawn = false;
      if (item.icon) {
        const img = this.assetLoader.getImage(`item_icon:${item.icon}`);
        if (img) {
          ctx.drawImage(img, x + pad, y + pad, iconSize, iconSize);
          iconDrawn = true;
        }
      }

      // Procedural icon fallback
      if (!iconDrawn && 'slot' in item) {
        const procIcon = getProceduralIcon((item as EquipmentItem).slot, item.rarity);
        if (procIcon) {
          ctx.drawImage(procIcon, x + pad, y + pad, iconSize, iconSize);
          iconDrawn = true;
        }
      }

      // Legacy fallback: colored square + emoji
      if (!iconDrawn) {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.roundRect(x + 6, y + 6, size - 12, size - 12, 3);
        ctx.fill();

        if ('slot' in item) {
          ctx.fillStyle = '#fff';
          ctx.font = `${Math.floor(size * 0.32)}px Arial`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(this.getSlotIcon((item as EquipmentItem).slot), x + size / 2, y + size / 2);
        }
      }

      // Rarity border glow (over the icon)
      if (item.rarity !== 'normal') {
        ctx.strokeStyle = color + '88';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(x + 2, y + 2, size - 4, size - 4, 4);
        ctx.stroke();
      }

      // Level badge
      if ('level' in item) {
        ctx.fillStyle = 'rgba(0,0,0,0.75)';
        ctx.beginPath();
        ctx.roundRect(x + size - 16, y + size - 14, 14, 12, 2);
        ctx.fill();

        ctx.fillStyle = '#fff';
        ctx.font = 'bold 9px Arial';
        ctx.textAlign = 'center';
        ctx.fillText((item as EquipmentItem).level.toString(), x + size - 9, y + size - 5);
      }
    } else if (equipSlot) {
      // Empty equipment slot: show faded fallback icon
      const fallbackPath = SLOT_FALLBACK_ICONS[equipSlot];
      if (fallbackPath) {
        const img = this.assetLoader.getImage(`item_icon:${fallbackPath}`);
        if (img) {
          ctx.globalAlpha = 0.25;
          const pad = 6;
          ctx.drawImage(img, x + pad, y + pad, size - pad * 2, size - pad * 2);
          ctx.globalAlpha = 1.0;
        } else {
          // Procedural fallback for empty slot
          const procIcon = getProceduralIcon(equipSlot, 'normal');
          if (procIcon) {
            ctx.globalAlpha = 0.25;
            const pad = 6;
            ctx.drawImage(procIcon, x + pad, y + pad, size - pad * 2, size - pad * 2);
            ctx.globalAlpha = 1.0;
          } else {
            ctx.fillStyle = '#333';
            ctx.font = `${Math.floor(size * 0.28)}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(this.getSlotIcon(equipSlot), x + size / 2, y + size / 2);
          }
        }
      }
    }
  }

  private drawFooter(ctx: CanvasRenderingContext2D): void {
    ctx.fillStyle = '#555';
    ctx.font = '11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      t('inventory.closeHint'),
      this.panelX + this.panelW / 2,
      this.panelY + this.panelH - 12
    );
  }

  private getSlotLabel(slot: EquipmentSlot): string {
    const labels: Record<EquipmentSlot, string> = {
      head: t('inventory.helmet'), chest: t('inventory.chestArmor'), gloves: t('inventory.gloves'), belt: t('inventory.belt'),
      boots: t('inventory.boots'), weapon: t('inventory.weapon'), offhand: t('inventory.shield'), amulet: t('inventory.amulet'),
      ring1: t('inventory.ring'), ring2: t('inventory.ring'), cape: t('inventory.cape')
    };
    return labels[slot] || slot;
  }

  private getSlotIcon(slot: EquipmentSlot): string {
    const icons: Record<EquipmentSlot, string> = {
      head: '⛑', chest: '🛡', gloves: '🧤', belt: '▬',
      boots: '👢', weapon: '⚔', offhand: '🛡', amulet: '◆',
      ring1: '◯', ring2: '◯', cape: '🧥'
    };
    return icons[slot] || '?';
  }

}
